import type { components } from "@dolfin/api-admin";
import { formatCurrency } from "@/lib/format";

export type ExpenseApprovalPolicyResponse = components["schemas"]["ExpenseApprovalPolicyResponse"];
export type ExpenseApprovalPolicyAction = components["schemas"]["ExpenseApprovalPolicyAction"];
export type ExpenseReceiptCondition = components["schemas"]["ExpenseReceiptCondition"];
export type ExpensePersonalSpendCondition = components["schemas"]["ExpensePersonalSpendCondition"];
export type UpsertExpenseApprovalPolicyRequest = components["schemas"]["UpsertExpenseApprovalPolicyRequest"];
export type ExpensePolicyProposalResponse = components["schemas"]["ExpensePolicyProposalResponse"];
export type ExpensePolicyClauseRefResponse = components["schemas"]["ExpensePolicyClauseRefResponse"];

export const MAX_EXPENSE_POLICY_APPROVERS = 5;
export const MAX_EXPENSE_POLICY_PRIORITY = 1000;

export const EXPENSE_ACTION_OPTIONS: {
  value: ExpenseApprovalPolicyAction;
  label: string;
  description: string;
}[] = [
  { value: "AutoApprove", label: "Auto-approve", description: "Post the expense without anyone reviewing it." },
  { value: "RequireApproval", label: "Require approval", description: "Hold the expense until an approver signs it off." },
  { value: "Flag", label: "Flag", description: "Flag the expense as a policy violation for someone to resolve." },
];

export const RECEIPT_CONDITION_OPTIONS: { value: ExpenseReceiptCondition; label: string }[] = [
  { value: "Any", label: "With or without a receipt" },
  { value: "Attached", label: "Receipt attached" },
  { value: "Missing", label: "No receipt" },
];

export const PERSONAL_SPEND_OPTIONS: { value: ExpensePersonalSpendCondition; label: string }[] = [
  { value: "Any", label: "Business and personal spend" },
  { value: "Business", label: "Business spend only" },
  { value: "Personal", label: "Personal spend only" },
];

export function expenseActionLabel(action: ExpenseApprovalPolicyAction | undefined): string {
  return EXPENSE_ACTION_OPTIONS.find((o) => o.value === action)?.label ?? action ?? "Unknown";
}

/** Badge tone per action, in the bill approval-policy idiom (border/bg/text at 25/15/600). */
export function expenseActionBadgeClass(action: ExpenseApprovalPolicyAction | undefined): string {
  switch (action) {
    case "AutoApprove":
      return "border-green-500/25 bg-green-500/15 text-green-600";
    case "RequireApproval":
      return "border-amber-500/25 bg-amber-500/15 text-amber-600";
    case "Flag":
      return "border-red-500/25 bg-red-500/15 text-red-600";
    default:
      return "";
  }
}

/** "v3". The version starts at 1 and goes up by one on every change. */
export function versionLabel(version: number | string | undefined | null): string {
  return version == null ? "" : `v${version}`;
}

function hasValue(v: number | string | null | undefined): v is number | string {
  return v != null && v !== "";
}

/** e.g. "£10.00+ GBP · Taxicabs · no receipt". Merchant and card scoping are shown separately. */
export function expenseConditionSummary(
  policy: {
    amountGte?: number | string | null;
    amountLte?: number | string | null;
    currency?: string | null;
    mccCodes?: string[];
    receiptCondition?: ExpenseReceiptCondition;
    personalSpend?: ExpensePersonalSpendCondition;
  },
  categoryLabel: (mcc: string) => string = (mcc) => mcc,
): string {
  const parts: string[] = [];
  const ccy = policy.currency ?? "";
  const gte = policy.amountGte;
  const lte = policy.amountLte;
  if (hasValue(gte) && hasValue(lte)) {
    parts.push(`${formatCurrency(gte, ccy)}-${formatCurrency(lte, ccy)} ${ccy}`.trim());
  } else if (hasValue(gte)) {
    parts.push(`${formatCurrency(gte, ccy)}+ ${ccy}`.trim());
  } else if (hasValue(lte)) {
    parts.push(`up to ${formatCurrency(lte, ccy)} ${ccy}`.trim());
  }

  const mccs = policy.mccCodes ?? [];
  if (mccs.length === 1) parts.push(categoryLabel(mccs[0]!));
  else if (mccs.length === 2) parts.push(`${categoryLabel(mccs[0]!)} or ${categoryLabel(mccs[1]!)}`);
  else if (mccs.length > 2) parts.push(`${categoryLabel(mccs[0]!)} +${mccs.length - 1} categories`);

  if (policy.receiptCondition === "Missing") parts.push("no receipt");
  else if (policy.receiptCondition === "Attached") parts.push("receipt attached");

  if (policy.personalSpend === "Personal") parts.push("personal spend");
  else if (policy.personalSpend === "Business") parts.push("business spend");

  return parts.length ? parts.join(" · ") : "Any expense";
}

/** Amounts and priority stay as the input strings; `toUpsertRequest` converts them. */
export interface ExpensePolicyDraft {
  name: string;
  priority: string;
  active: boolean;
  amountGte: string;
  amountLte: string;
  currency: string;
  mccCodes: string[];
  merchantIds: string[];
  cardConnectionIds: string[];
  spenderUserIds: string[];
  receiptCondition: ExpenseReceiptCondition;
  personalSpend: ExpensePersonalSpendCondition;
  action: ExpenseApprovalPolicyAction;
  approverUserIds: string[];
}

export function emptyDraft(priority: number): ExpensePolicyDraft {
  return {
    name: "",
    priority: String(priority),
    active: true,
    amountGte: "",
    amountLte: "",
    currency: "",
    mccCodes: [],
    merchantIds: [],
    cardConnectionIds: [],
    spenderUserIds: [],
    receiptCondition: "Any",
    personalSpend: "Any",
    action: "RequireApproval",
    approverUserIds: [],
  };
}

export function draftFromPolicy(policy: ExpenseApprovalPolicyResponse): ExpensePolicyDraft {
  return {
    name: policy.name,
    priority: String(policy.priorityOrder),
    active: policy.active,
    amountGte: hasValue(policy.amountGte) ? String(policy.amountGte) : "",
    amountLte: hasValue(policy.amountLte) ? String(policy.amountLte) : "",
    currency: policy.currency ?? "",
    mccCodes: policy.mccCodes ?? [],
    merchantIds: policy.merchantIds ?? [],
    cardConnectionIds: policy.cardConnectionIds ?? [],
    spenderUserIds: policy.spenderUserIds ?? [],
    receiptCondition: policy.receiptCondition,
    personalSpend: policy.personalSpend,
    action: policy.action,
    approverUserIds: policy.approverUserIds ?? [],
  };
}

export function draftFromProposal(proposal: ExpensePolicyProposalResponse, index: number): ExpensePolicyDraft {
  return {
    name: proposal.name,
    priority: String(proposal.priorityOrder ?? (index + 1) * 10),
    active: true,
    amountGte: hasValue(proposal.amountGte) ? String(proposal.amountGte) : "",
    amountLte: hasValue(proposal.amountLte) ? String(proposal.amountLte) : "",
    currency: proposal.currency ?? "",
    mccCodes: proposal.mccCodes ?? [],
    merchantIds: proposal.merchantIds ?? [],
    cardConnectionIds: [],
    spenderUserIds: [],
    receiptCondition: proposal.receiptCondition ?? "Any",
    personalSpend: proposal.personalSpend ?? "Any",
    action: proposal.action ?? "RequireApproval",
    approverUserIds: proposal.approverUserIds ?? [],
  };
}

function numOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return isNaN(n) ? null : n;
}

/** Client-side checks that mirror the backend's validation. Returns the first problem, or null. */
export function validateDraft(draft: ExpensePolicyDraft): string | null {
  if (!draft.name.trim()) return "A policy name is required.";
  const priority = Number(draft.priority);
  if (
    draft.priority.trim() === "" ||
    !Number.isInteger(priority) ||
    priority < 0 ||
    priority > MAX_EXPENSE_POLICY_PRIORITY
  ) {
    return `Priority order must be a whole number between 0 and ${MAX_EXPENSE_POLICY_PRIORITY}.`;
  }
  const gte = numOrNull(draft.amountGte);
  const lte = numOrNull(draft.amountLte);
  if ((gte != null && gte < 0) || (lte != null && lte < 0)) return "Amount bounds must not be negative.";
  if ((gte != null || lte != null) && !draft.currency.trim()) {
    return "A currency is required when an amount condition is set.";
  }
  if (gte != null && lte != null && gte > lte) return "The lower amount bound must not exceed the upper bound.";
  if (draft.mccCodes.some((m) => !/^\d{4}$/.test(m))) return "Spend categories must be four-digit codes.";
  if (draft.action === "RequireApproval" && draft.approverUserIds.length > MAX_EXPENSE_POLICY_APPROVERS) {
    return `At most ${MAX_EXPENSE_POLICY_APPROVERS} approvers are allowed.`;
  }
  return null;
}

export function toUpsertRequest(draft: ExpensePolicyDraft): UpsertExpenseApprovalPolicyRequest {
  const gte = numOrNull(draft.amountGte);
  const lte = numOrNull(draft.amountLte);
  const hasAmount = gte != null || lte != null;
  return {
    name: draft.name.trim(),
    priorityOrder: Number(draft.priority),
    active: draft.active,
    amountGte: gte,
    amountLte: lte,
    currency: hasAmount ? draft.currency.trim().toUpperCase() : null,
    mccCodes: draft.mccCodes,
    merchantIds: draft.merchantIds,
    cardConnectionIds: draft.cardConnectionIds,
    spenderUserIds: draft.spenderUserIds,
    receiptCondition: draft.receiptCondition,
    personalSpend: draft.personalSpend,
    action: draft.action,
    // Approvers only carry meaning for RequireApproval.
    approverUserIds: draft.action === "RequireApproval" ? draft.approverUserIds : [],
  };
}

/** Where a clause sits in the source document, e.g. "page 3". Pages are only known for PDFs. */
export function clausePageLabel(clause: { page?: number | string | null } | null | undefined): string | null {
  return clause && hasValue(clause.page) ? `page ${clause.page}` : null;
}
